import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// UPDATED: Added localStorage persistence - Version 3.0
/**
 * Типы
 */
export type Role = 'trainer' | 'client' | 'waiter';

export interface User {
  id: number;
  firstName: string;
  lastName?: string;
  middleName?: string;
  phone: string;
  role: Role;
  specName?: string; // Для тренеров - специализация
  department?: {
    dep_id: number;
    dep_name: string;
  };
  services?: any[]; // Услуги тренера
  employeeId?: number; // ID сотрудника для тренеров
  bonusBalance?: number; // Баланс бонусов клиента
  email?: string; // Email клиента
  avatar?: string; // Аватар
  birthday?: string; // День рождения
  city?: string; // Город
  address?: string; // Адрес
  isAdmin?: boolean; // Админ
  createdAt?: string; // Дата создания
}

interface AuthStore extends AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  apiClient: UniverseApiClient; // API клиент
  // Auth actions
  sendSMS: (phone: string) => Promise<{ success: boolean; error?: string; userType?: 'employee' | 'client' | 'new' }>;
  verifySMS: (code: string) => Promise<{ success: boolean; error?: string; userType?: 'employee' | 'client' }>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
  getApiClient: () => UniverseApiClient;
  // Additional auth methods
  login?: (phone: string, password?: string) => Promise<void>;
  register?: (userData: any) => Promise<void>;
  initializeApi?: (baseUrl: string) => void;
  // API wrapper methods
  checkPhoneExists?: (phone: string) => Promise<{ exists: boolean; clientInfo?: any; userType?: 'employee' | 'client' }>;
  findClient?: (phone: string) => Promise<any>;
  sendSMSCode?: (phone: string) => Promise<{ success: boolean; error?: string }>;
  verifySMSCode?: (phone: string, code: string) => Promise<{ success: boolean; error?: string }>;
  registerWithSMS?: (phone: string, firstName: string, lastName: string) => Promise<{ success: boolean; error?: string }>;
  loginWithSMS?: (phone: string, code: string) => Promise<void>;
  // Bonus methods
  getBonusBalance?: (phone: string) => Promise<number>;
  useBonusesForPayment?: (phone: string, amount: number) => Promise<boolean>;
  updateBonusBalance?: () => Promise<boolean>;
  // Restaurant
  createRestaurantOrder?: (orderData: any) => Promise<any>;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Простая обёртка над API (адаптируйте под ваши реальные методы)
 */
class UniverseApiClient {
  private baseUrl: string;
  private login: string;
  private password: string;
  private clubId: string | number | undefined;
  private token: string | null = null;

  constructor(opts: { baseUrl: string; login: string; password: string; clubId?: string | number }) {
    this.baseUrl = opts.baseUrl;
    this.login = opts.login;
    this.password = opts.password;
    this.clubId = opts.clubId;
  }

  async getToken(): Promise<string> {
    if (this.token) return this.token;
    // Получаем токен через наш API сервер
    const resp = await fetch(`${this.baseUrl}/universe/token`, {
      method: 'GET'
    });
    if (!resp.ok) throw new Error('Не удалось получить токен');
    const data = await resp.json();
    this.token = data?.token || data?.code || null;
    if (!this.token) throw new Error('Токен пуст');
    return this.token;
  }

  private async makeRequest(method: string, payload: any): Promise<any> {
    const resp = await fetch(`${this.baseUrl}/universe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ method, ...payload })
    });

    // Всегда пытаемся получить JSON, даже если HTTP статус не ok
    let data;
    try {
      data = await resp.json();
    } catch (e) {
      // Если не удалось распарсить JSON - выбрасываем HTTP ошибку
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      }
      throw e;
    }

    // Проверяем код ошибки Universe API
    if (data.errorCode && data.errorCode !== '0' && data.errorCode !== 0) {
      // Если это ошибка "не найдено" - не бросаем исключение, просто возвращаем
      if (data.errorCode === '224' || data.errorCode === 224) {
        return data;
      }
      throw new Error(data.message || data.error || 'Universe API error');
    }

    return data;
  }

  // ===== Клиенты
  generatePhoneFormats(phone: string): string[] {
    const digits = (phone || '').replace(/\D/g, '');
    const last10 = digits.slice(-10);
    const with7 = `7${last10}`;
    const with8 = `8${last10}`;
    const plus7 = `+7${last10}`;
    const plus8 = `+8${last10}`;
    const local = last10; // 10-значный
    return Array.from(new Set([digits, with7, with8, plus7, plus8, local]));
  }

  // ===== Сотрудники
  private normalizeToLast10Digits(input: string): string | null {
    const digits = String(input || '').replace(/\D/g, '');
    if (digits.length < 10) return null;
    return digits.slice(-10);
  }

  private extractPhoneCandidatesFromObject(obj: any): string[] {
    const raw = JSON.stringify(obj);
    const matches = raw.match(/\d{10,12}/g) || [];
    const result = new Set<string>();
    for (const m of matches) {
      const last10 = this.normalizeToLast10Digits(m);
      if (last10) result.add(last10);
    }
    return Array.from(result);
  }

  // Заглушка: получите список сотрудников из вашего API
  async getEmployees(): Promise<any[]> {
    const data = await this.makeRequest('get_employees', { clubId: this.clubId });
    // Ожидаем что data.Employees — массив; подстройте при необходимости
    return Array.isArray(data?.Employees) ? data.Employees : Array.isArray(data) ? data : [];
  }

  async findEmployeeByPhone(phone: string): Promise<any | null> {
    const inputLast10 = this.normalizeToLast10Digits(phone);
    console.log('[findEmployeeByPhone] Ищем номер:', phone, '→ нормализован:', inputLast10);
    if (!inputLast10) return null;

    const employees = await this.getEmployees();
    console.log('[findEmployeeByPhone] Получено сотрудников:', employees.length);

    // Выведем первого сотрудника для проверки структуры
    if (employees.length > 0) {
      console.log('[findEmployeeByPhone] Пример сотрудника:', JSON.stringify(employees[0], null, 2));
    }

    for (let i = 0; i < employees.length; i++) {
      const emp = employees[i];

      // Проверяем прямое совпадение в поле phone_number
      if (emp.phone_number) {
        const empLast10 = this.normalizeToLast10Digits(emp.phone_number);
        console.log(`[findEmployeeByPhone] Сотрудник ${i}: Id=${emp.Id}, phone_number="${emp.phone_number}" → "${empLast10}" vs "${inputLast10}"`);
        if (empLast10 === inputLast10) {
          console.log('[findEmployeeByPhone] ✓ НАЙДЕНО СОВПАДЕНИЕ! Id:', emp.Id, 'Имя:', emp.first_name, emp.last_name);
          return emp;
        }
      } else {
        console.log(`[findEmployeeByPhone] Сотрудник ${i}: Id=${emp.Id}, НЕТ phone_number`);
      }

      // Дополнительная проверка через извлечение всех телефонных кандидатов
      const phones = this.extractPhoneCandidatesFromObject(emp);
      if (phones.length > 0) {
        console.log(`[findEmployeeByPhone] Сотрудник ${i}: извлечённые телефоны:`, phones);
      }
      if (phones.some((p) => p === inputLast10)) {
        console.log('[findEmployeeByPhone] ✓ НАЙДЕНО через extractPhoneCandidates! Id:', emp.Id);
        return emp;
      }
    }
    console.log('[findEmployeeByPhone] ✗ Сотрудник не найден');
    return null;
  }

  /**
   * Универсальная проверка телефона:
   * 1) Ищем среди сотрудников → userType: 'employee'
   * 2) Не нашли → ищем среди клиентов → userType: 'client'
   * 3) Не нашли нигде → exists: false
   */
  async checkPhoneExists(
    phone: string
  ): Promise<{ exists: boolean; clientInfo?: any; userType?: 'employee' | 'client' }> {
    try {
      console.log('[checkPhoneExists] Проверяем номер:', phone);

      // 1) сотрудники
      const emp = await this.findEmployeeByPhone(phone);
      if (emp) {
        console.log('[checkPhoneExists] Найден сотрудник:', emp);
        return { exists: true, clientInfo: emp, userType: 'employee' };
      }
      console.log('[checkPhoneExists] Сотрудник не найден, ищем клиента');

      // 2) клиенты
      const formats = this.generatePhoneFormats(phone);
      console.log('[checkPhoneExists] Форматы для поиска клиента:', formats);
      for (const f of formats) {
        try {
          const resp = await this.makeRequest('FindClient', { Phone: f });
          if ((resp?.errorCode === '0' || resp?.errorCode === 0) && resp?.Client) {
            console.log('[checkPhoneExists] Найден клиент:', resp.Client);
            return { exists: true, clientInfo: resp.Client, userType: 'client' };
          }
        } catch (_) {
          // продолжаем пробовать другие форматы
        }
      }

      console.log('[checkPhoneExists] Номер не найден');
      return { exists: false };
    } catch (e) {
      console.error('checkPhoneExists error', e);
      return { exists: false };
    }
  }

  // Отправка SMS через RedSMS API
  async sendSmsCode(phone: string, code: string): Promise<void> {
    try {
      // Логируем для отладки (но код не будет в production логах на сервере)
      console.info(`[SMS] Отправка кода на номер ${phone}`);
      console.info(`[DEV] SMS код: ${code}`); // Для отладки - можно будет убрать

      // Форматируем номер для RedSMS (международный формат: 7XXXXXXXXXX)
      let formattedPhone = phone.replace(/\D/g, ''); // Убираем все нецифровые символы

      // Приводим к формату 7XXXXXXXXXX
      if (formattedPhone.startsWith('8') && formattedPhone.length === 11) {
        // 8XXXXXXXXXX → 7XXXXXXXXXX
        formattedPhone = '7' + formattedPhone.slice(1);
      } else if (formattedPhone.startsWith('9') && formattedPhone.length === 11) {
        // 9XXXXXXXXXX → берем первые 10 цифр и добавляем 7
        // 97812300000 → 7 + (первые 10) = 79781230000
        formattedPhone = '7' + formattedPhone.slice(0, 10);
      } else if (formattedPhone.startsWith('7') && formattedPhone.length === 11) {
        // 7XXXXXXXXXX → оставляем как есть
        formattedPhone = formattedPhone;
      } else if (formattedPhone.startsWith('9') && formattedPhone.length === 10) {
        // 9XXXXXXXXX → 79XXXXXXXXX
        formattedPhone = '7' + formattedPhone;
      } else if (formattedPhone.length === 10) {
        // XXXXXXXXXX → 7XXXXXXXXXX
        formattedPhone = '7' + formattedPhone;
      } else if (formattedPhone.length > 11) {
        // Слишком длинный номер - берем первые 10 цифр
        formattedPhone = '7' + formattedPhone.slice(0, 10);
      }

      console.info(`[SMS] Форматированный номер: ${formattedPhone}`);

      const response = await fetch(`${this.baseUrl}/redsms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          route: 'sms',
          sender: 'LevelBeach', // или используйте цифровой отправитель если буквенный не активирован
          text: `Ваш код подтверждения: ${code}`,
          to: formattedPhone
        })
      });

      if (!response.ok) {
        throw new Error(`RedSMS API error: ${response.status}`);
      }

      const result = await response.json();
      console.info('[SMS] ✅ SMS отправлена успешно:', result);
    } catch (error) {
      console.error('[SMS] ❌ Ошибка отправки SMS:', error);
      // Не бросаем ошибку, чтобы пользователь мог продолжить (код можно увидеть в консоли для отладки)
    }
  }

  // Получить полную информацию о клиенте по ClientId
  async getClient(clientId: number): Promise<any> {
    return await this.makeRequest('get_client', { ClientId: clientId });
  }

  // Получить информацию о клиенте по телефону
  async getClientInfo(phone: string): Promise<any | null> {
    const formats = this.generatePhoneFormats(phone);
    for (const f of formats) {
      try {
        const resp = await this.makeRequest('FindClient', { Phone: f });
        if ((resp?.errorCode === '0' || resp?.errorCode === 0) && resp?.Client) {
          return resp.Client;
        }
      } catch (_) {
        // продолжаем пробовать другие форматы
      }
    }
    return null;
  }

  // Получить записи клиента (включая корты и групповые занятия)
  async getClientBookings(clientPhone: string, dateFrom: string, dateTo: string): Promise<any[]> {
    try {
      // Сначала получаем ClientId по номеру телефона
      const clientInfo = await this.getClientInfo(clientPhone);
      if (!clientInfo || !clientInfo.ClientId) {
        return [];
      }

      // Используем clients_booking_ex для получения ВСЕХ типов записей
      const response = await this.makeRequest('clients_booking_ex', {
        first_date: dateFrom,
        last_date: dateTo,
        clients: [clientInfo.ClientId]
      });

      if (response.errorCode === '0') {
        // Объединяем обычные записи, групповые занятия и корты
        const regularBookings = response.clients_booking || [];
        const groupLessons = response.clients_lesson || [];
        const courtsBookings = response.courts_booking || [];

        // Преобразуем групповые занятия в формат обычных записей
        const transformedLessons = groupLessons.map((lesson: any) => ({
          Id: lesson.lesson_id,
          time_from: `${lesson.date} ${lesson.start_time}`,
          time_to: `${lesson.date} ${lesson.end_time}`,
          EmpId: lesson.trainer_id,
          ClientId: clientInfo.ClientId,
          comment: '',
          DeviceId: null,
          Services: [{
            service_id: lesson.service.service_id,
            service_name: lesson.service.service_name,
            is_group: 1
          }]
        }));

        // Преобразуем записи на корты
        const transformedCourts = courtsBookings.map((court: any) => ({
          Id: court.Id,
          time_from: court.time_from,
          time_to: court.time_to,
          EmpId: court.EmpId,
          ClientId: court.ClientId,
          comment: court.comment || '',
          DeviceId: court.court_id,
          Services: [],
          court_name: court.court_name || 'Корт',
          is_court: true
        }));

        const allBookings = [...regularBookings, ...transformedLessons, ...transformedCourts];
        return allBookings;
      }

      return [];
    } catch (e) {
      console.error('getClientBookings error', e);
      return [];
    }
  }

  // Получить карты клиента
  async getClientCards(clientId: number): Promise<any[]> {
    try {
      const resp = await this.makeRequest('get_client_cards', { ClientId: clientId });
      return Array.isArray(resp?.cards) ? resp.cards : Array.isArray(resp) ? resp : [];
    } catch (e) {
      console.error('getClientCards error', e);
      return [];
    }
  }

  // Удалить запись
  async deleteBooking(bookingId: string): Promise<void> {
    // Форматируем GUID с фигурными скобками согласно Universe API
    const formattedId = bookingId.startsWith('{') ? bookingId : `{${bookingId}}`;
    await this.makeRequest('delete_booking', { id: formattedId });
  }

  // Удалить запись на корт
  async deleteCourtBooking(bookingId: string): Promise<void> {
    // Форматируем GUID с фигурными скобками согласно Universe API
    const formattedId = bookingId.startsWith('{') ? bookingId : `{${bookingId}}`;
    await this.makeRequest('delete_court_booking', { id: formattedId });
  }

  // Отписать клиента от группового занятия
  async unregisterClientFromTraining(clientId: number, trainingId: string): Promise<void> {
    // Форматируем GUID с фигурными скобками согласно Universe API
    const formattedLessonId = trainingId.startsWith('{') ? trainingId : `{${trainingId}}`;
    await this.makeRequest('unregistration_client_traning', {
      client_id: clientId,
      lesson_id: formattedLessonId
    });
  }

  // ===== ДОПОЛНИТЕЛЬНЫЕ МЕТОДЫ ИЗ authStore1.ts =====

  // Установить токен
  setToken(token: string) {
    this.token = token;
  }

  // Найти клиента (возвращает массив)
  async findClient(params: { Phone: string } | string): Promise<any[]> {
    const phone = typeof params === 'string' ? params : params.Phone;
    const formats = this.generatePhoneFormats(phone);

    for (const f of formats) {
      try {
        const resp = await this.makeRequest('FindClient', { Phone: f });
        if ((resp?.errorCode === '0' || resp?.errorCode === 0) && resp?.Client) {
          return [resp.Client];
        }
      } catch (_) {
        continue;
      }
    }
    return [];
  }

  // Создать клиента
  async createClient(clientData: any): Promise<any> {
    const response = await this.makeRequest('create_client', clientData);

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to create client');
    }

    return response;
  }

  // Получить список услуг
  async getServiceList(): Promise<any[]> {
    try {
      const resp = await this.makeRequest('service_list', {});
      if (resp.errorCode === '0' && resp.booking_services) {
        return resp.booking_services;
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // Получить услуги для бронирования
  async getBookingServices(): Promise<any[]> {
    try {
      const resp = await this.makeRequest('service_list', {});
      if (resp.errorCode === '0' && resp.booking_services) {
        return resp.booking_services;
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // Создать бронирование
  async createBooking(bookingData: any): Promise<any> {
    const apiParams: any = {
      ClientId: bookingData.ClientId,
      time_from: bookingData.date_time_start,
      time_to: bookingData.date_time_end,
      services: [] // Universe API requires this parameter
    };

    if (bookingData.EmpId) {
      apiParams.EmpId = bookingData.EmpId;
    }

    if (bookingData.ServiceId) {
      apiParams.services = [bookingData.ServiceId];
    }

    if (bookingData.DeviceId) {
      apiParams.DeviceId = bookingData.DeviceId;
    }

    if (bookingData.note) {
      apiParams.comment = bookingData.note;
    }

    console.log('[createBooking] API Request:', apiParams);

    const response = await this.makeRequest('create_booking', apiParams);

    console.log('[createBooking] API Response:', response);

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to create booking');
    }

    return response;
  }

  // Получить свободное время сотрудника
  async getEmpFreeTime(empId: number, date: string): Promise<any[]> {
    try {
      const resp = await this.makeRequest('emp_free_time', {
        first_date: date,
        last_date: date,
        employees: [empId]
      });

      if (resp.errorCode === '0') {
        return resp.emp_free_time || [];
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // Получить список устройств/кабинетов
  async getDevices(): Promise<any[]> {
    try {
      const resp = await this.makeRequest('get_devices', {});

      if (resp.errorCode === '0') {
        return resp.devices || [];
      }
      return [];
    } catch (e) {
      console.error('[getDevices] Error:', e);
      return [];
    }
  }

  // Получить свободное время устройств/кабинетов
  async getDeviceFreeTime(devices: number[], date: string): Promise<any[]> {
    try {
      console.log('[getDeviceFreeTime] Request:', { devices, date });
      const resp = await this.makeRequest('device_free_time', {
        first_date: date,
        last_date: date,
        devices: devices
      });

      console.log('[getDeviceFreeTime] Response:', resp);

      if (resp.errorCode === '0') {
        console.log('[getDeviceFreeTime] Device free time data:', resp.device_free_time);
        return resp.device_free_time || [];
      }
      console.log('[getDeviceFreeTime] Error code:', resp.errorCode, 'Message:', resp.message);
      return [];
    } catch (e) {
      console.error('[getDeviceFreeTime] Error:', e);
      return [];
    }
  }

  // Получить свободное время кортов
  async getCourtFreeTime(courts: number[], date: string): Promise<any[]> {
    try {
      const resp = await this.makeRequest('court_free_time', {
        first_date: date,
        last_date: date,
        courts: courts
      });

      if (resp.errorCode === '0') {
        return resp.court_free_time || [];
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // Создать бронирование корта
  async createCourtBooking(bookingData: any): Promise<any> {
    console.log('[createCourtBooking] API Request:', bookingData);
    const response = await this.makeRequest('create_court_booking', bookingData);
    console.log('[createCourtBooking] API Response:', response);

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to create court booking');
    }

    return response;
  }

  // Создать бронирование тренера
  async createTrainerBooking(bookingData: any): Promise<any> {
    return await this.createBooking(bookingData);
  }

  // ⭐ Получить групповые занятия
  async getGroupLessons(firstDate?: string, lastDate?: string): Promise<any[]> {
    try {
      const resp = await this.makeRequest('get_lessons', {
        ...(firstDate && { first_date: firstDate }),
        ...(lastDate && { last_date: lastDate })
      });

      if (resp.errorCode === '0') {
        return resp.lessons || [];
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // ⭐ Записать клиента на групповое занятие
  async registerClientForTraining(clientId: number, lessonId: number | string): Promise<any> {
    const response = await this.makeRequest('registration_client_traning', {
      client_id: clientId,
      lesson_id: lessonId
    });

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      // Проверяем если ошибка связана с отсутствием карты
      const message = response.message || '';
      if (message.includes('card') || message.includes('карт') || message.includes('Card')) {
        throw new Error('Для записи на групповые занятия необходимо приобрести клубную карту');
      }

      throw new Error(response.message || 'Failed to register for training');
    }

    return response;
  }

  // ⭐ Получить баланс бонусов клиента
  async getBonusBalance(phone: string): Promise<number> {
    try {
      // 1. Найти клиента по номеру телефона
      const findClientResponse = await this.makeRequest('FindClient', { Phone: phone });

      if (findClientResponse.errorCode !== '0' || !findClientResponse.Client?.ClientId) {
        return 0;
      }

      const clientId = findClientResponse.Client.ClientId;

      // 2. Получить полную информацию о клиенте
      const clientData = await this.getClient(clientId);

      // Ищем поле с бонусами
      const bonusBalance = clientData?.bonus_balance ||
                          clientData?.bonusBalance ||
                          clientData?.Client?.bonus_balance ||
                          clientData?.Client?.bonusBalance ||
                          0;

      return bonusBalance;
    } catch (e) {
      return 0;
    }
  }

  // Использовать бонусы для оплаты
  async useBonusesForPayment(phone: string, amount: number): Promise<boolean> {
    try {
      const response = await this.makeRequest('use_bonuses', {
        phone: phone,
        amount: amount
      });

      return response.errorCode === '0' || response.errorCode === 0;
    } catch (e) {
      return false;
    }
  }

  // ⭐ Создать заказ в ресторане
  async createRestaurantOrder(orderData: any): Promise<any> {
    try {
      const response = await this.makeRequest('create_restaurant_order', orderData);

      if (response.errorCode === '0' || response.errorCode === 0) {
        return response;
      }

      throw new Error(response.message || 'Failed to create restaurant order');
    } catch (error) {
      throw error;
    }
  }

  // Получить типы карт
  async getCardTypes(): Promise<any[]> {
    try {
      const resp = await this.makeRequest('get_cardtypes', {});

      if (resp.errorCode === '0' && resp.card_list) {
        return resp.card_list;
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  // Купить карту
  async buyCard(clientId: number, cardTypeId: number, activate: boolean = false): Promise<any> {
    const response = await this.makeRequest('buy_card', {
      IdClient: clientId,
      IdTypeCard: cardTypeId,
      Activate: activate
    });

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to buy card');
    }

    return response;
  }

  // Заморозить карту
  async freezeCard(clientId: number, cardId: number, countDays: number, freezeDate: string): Promise<any> {
    const response = await this.makeRequest('freeze_card', {
      IdClient: clientId,
      IdCard: cardId,
      count_days: countDays,
      freeze_date: freezeDate
    });

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to freeze card');
    }

    return response;
  }

  // Получить способ оплаты клиента
  async getClientPaymentMethod(clientId: number): Promise<any | null> {
    try {
      const response = await this.makeRequest('get_client', { ClientId: clientId });

      if (response.errorCode === '0' && response.note) {
        try {
          return JSON.parse(response.note);
        } catch {
          return null;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // Обновить способ оплаты клиента
  async updateClientPaymentMethod(clientId: number, paymentData: any): Promise<void> {
    const response = await this.makeRequest('update_client', {
      ClientId: clientId,
      note: JSON.stringify(paymentData)
    });

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to update payment method');
    }
  }

  // Создать зачисление
  async createEnrolment(clientId: number, money: number, extId: string, note?: string): Promise<any> {
    const response = await this.makeRequest('Create_Enrolment', {
      ClientId: clientId,
      Money: money,
      type: 0,
      ExtId: extId,
      note: note || ''
    });

    if (response.errorCode !== '0' && response.errorCode !== 0) {
      throw new Error(response.message || 'Failed to create enrolment');
    }

    return response;
  }
}

// Инициализируйте ваш клиент (используем /api для локальных запросов через Vite proxy)
const apiClient = new UniverseApiClient({
  baseUrl: '/api',
  login: 'lvlsport991',
  password: 'zSHMaQPM',
  clubId: undefined
});

// ===== Вспомогательные функции для локального хранения кода =====
const SMS_KEY = 'sms_verification';
const SMS_TTL_MS = 5 * 60 * 1000; // 5 минут

function setSmsStorage(data: any) {
  localStorage.setItem(SMS_KEY, JSON.stringify(data));
}

function getSmsStorage(): any | null {
  const raw = localStorage.getItem(SMS_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function clearSmsStorage() {
  localStorage.removeItem(SMS_KEY);
}

function randomCode(): string {
  // 4-значный код
  return String(Math.floor(1000 + Math.random() * 9000));
}

// ===== Zustand Store с localStorage persistence =====
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      apiClient: apiClient, // Добавляем API клиент в state

  /**
   * Шаг 1: пользователь вводит телефон →
   *  - проверяем сотрудников, затем клиентов
   *  - если нашли, отправляем SMS, сохраняем userType + данные
   *  - если не нашли — возвращаем ошибку (UI может открыть регистрацию)
   */
  async sendSMS(phone: string) {
    console.log('[authStore.sendSMS] Вызван с номером:', phone);
    set({ isLoading: true });
    try {
      const phoneCheck = await apiClient.checkPhoneExists(phone);

      if (!phoneCheck.exists) {
        // Не нашли ни среди сотрудников, ни среди клиентов
        // Можно сразу показать форму регистрации
        const code = randomCode();
        const payload = {
          phone,
          code,
          timestamp: Date.now(),
          userType: 'new' as const, // для UI — новая регистрация
          clientData: null
        };
        setSmsStorage(payload);
        await apiClient.sendSmsCode(phone, code);
        set({ isLoading: false });
        return { success: false, error: 'Номер не найден в системе', userType: 'new' };
      }

      // Нашли сотрудника или клиента
      const code = randomCode();
      const smsStorage = {
        phone,
        code,
        timestamp: Date.now(),
        userType: phoneCheck.userType,
        clientData: phoneCheck.clientInfo
      };
      setSmsStorage(smsStorage);
      await apiClient.sendSmsCode(phone, code);

      set({ isLoading: false });
      return { success: true, userType: phoneCheck.userType };
    } catch (e: any) {
      console.error('sendSMS error', e);
      set({ isLoading: false });
      return { success: false, error: e?.message || 'Ошибка отправки СМС' };
    }
  },

  /**
   * Шаг 2: пользователь вводит код из SMS →
   *  - сверяем код и TTL
   *  - если userType === 'employee' → создаём пользователя-«тренера»
   *  - если userType === 'client'   → создаём пользователя-«клиента»
   *  Возвращаем userType для роутинга (UI решает, какой раздел открыть)
   */
  async verifySMS(code: string) {
    set({ isLoading: true });
    try {
      const saved = getSmsStorage();
      if (!saved) {
        set({ isLoading: false });
        return { success: false, error: 'Код не запрошен' };
      }

      const now = Date.now();
      if (now - saved.timestamp > SMS_TTL_MS) {
        clearSmsStorage();
        set({ isLoading: false });
        return { success: false, error: 'Код истёк, запросите заново' };
      }

      if (String(code) !== String(saved.code)) {
        set({ isLoading: false });
        return { success: false, error: 'Неверный код' };
      }

      const phone: string = saved.phone;
      const userType: 'employee' | 'client' | 'new' | undefined = saved.userType;

      // Ветка «сотрудник» (тренер)
      if (userType === 'employee') {
        const emp = saved.clientData || {};
        const firstName = emp?.first_name || emp?.FirstName || emp?.name || 'Тренер';
        const lastName = emp?.last_name || emp?.LastName || '';
        const middleName = emp?.middle_name || emp?.MiddleName;
        const specName = emp?.spec_name || emp?.specName || 'Тренер';
        // Universe API возвращает Id (с большой буквы) для сотрудников
        const id = Number(emp?.Id ?? emp?.id ?? emp?.EmployeeId ?? Date.now());

        console.log('🔍 [authStore] Данные сотрудника из Universe API:', emp);
        console.log('🔍 [authStore] Извлеченный ID сотрудника:', id);
        console.log('🔍 [authStore] Department:', emp?.department);

        const user: User = {
          id,
          firstName,
          lastName,
          middleName,
          phone,
          role: 'trainer',
          specName,
          department: emp?.department,
          services: emp?.services || [],
          employeeId: id
        };

        console.log('✅ [verifySMS] Создан пользователь СОТРУДНИК/ТРЕНЕР:', user);
        console.warn('🎯 РОЛЬ УСТАНОВЛЕНА:', user.role);
        console.log('🔍 [authStore] Department в user:', user.department);

        set({ user, token: 'employee-session', isAuthenticated: true, isLoading: false });
        clearSmsStorage();
        return { success: true, userType: 'employee' };
      }

      // Ветка «клиент»
      if (userType === 'client') {
        const cl = saved.clientData || {};
        const firstName = cl?.first_name || cl?.FirstName || cl?.name || 'Клиент';
        const lastName = cl?.last_name || cl?.LastName || '';
        const middleName = cl?.middle_name || cl?.MiddleName;
        const id = Number(cl?.ClientId ?? cl?.id ?? Date.now());

        const user: User = {
          id,
          firstName,
          lastName,
          middleName,
          phone,
          role: 'client'
        };

        set({ user, token: 'client-session', isAuthenticated: true, isLoading: false });
        clearSmsStorage();
        return { success: true, userType: 'client' };
      }

      // Ветка «новый пользователь» — UI должен открыть форму регистрации клиента
      set({ isLoading: false });
      return { success: false, error: 'Номер не найден. Требуется регистрация.', userType: 'client' };
    } catch (e: any) {
      console.error('verifySMS error', e);
      set({ isLoading: false });
      return { success: false, error: e?.message || 'Ошибка подтверждения кода' };
    }
  },

      logout() {
        clearSmsStorage();
        localStorage.removeItem('auth-storage'); // Очищаем persist storage
        set({ user: null, token: null, isAuthenticated: false });
      },

      updateUser(data: Partial<User>) {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...data } });
        }
      },

      getApiClient() {
        return apiClient;
      },

      // ===== ДОПОЛНИТЕЛЬНЫЕ МЕТОДЫ ДЛЯ СОВМЕСТИМОСТИ С authStore1.ts =====

      // Проверка существования телефона
      checkPhoneExists: async (phone: string) => {
        try {
          return await apiClient.checkPhoneExists(phone);
        } catch (e) {
          return { exists: false };
        }
      },

      // Найти клиента
      findClient: async (phone: string) => {
        try {
          const clients = await apiClient.findClient(phone);
          return clients.length > 0 ? clients[0] : null;
        } catch (e) {
          return null;
        }
      },

      // Aliases для LoginPage
      sendSMSCode: async (phone: string) => {
        return await get().sendSMS(phone);
      },

      loginWithSMS: async (phone: string, code: string) => {
        const result = await get().verifySMS(code);
        if (!result.success) {
          throw new Error(result.error || 'Ошибка авторизации');
        }
      },

      // Получить баланс бонусов
      getBonusBalance: async (phone: string): Promise<number> => {
        try {
          return await apiClient.getBonusBalance(phone);
        } catch (e) {
          return 0;
        }
      },

      // Использовать бонусы
      useBonusesForPayment: async (phone: string, amount: number): Promise<boolean> => {
        try {
          return await apiClient.useBonusesForPayment(phone, amount);
        } catch (e) {
          return false;
        }
      },

      // Обновить баланс бонусов
      updateBonusBalance: async (): Promise<boolean> => {
        const { user } = get();
        if (!user) return false;

        try {
          const bonusBalance = await apiClient.getBonusBalance(user.phone);
          get().updateUser({ bonusBalance });
          return true;
        } catch (e) {
          return false;
        }
      },

      // Создать заказ в ресторане
      createRestaurantOrder: async (orderData: any): Promise<any> => {
        try {
          return await apiClient.createRestaurantOrder(orderData);
        } catch (error) {
          throw error;
        }
      },

      // Регистрация с SMS
      registerWithSMS: async (phone: string, firstName: string, lastName: string) => {
        try {
          set({ isLoading: true });

          // Создаем клиента
          await apiClient.createClient({
            first_name: firstName,
            last_name: lastName,
            Phone: phone,
            SmsInfo: 1
          });

          // Отправляем SMS
          const result = await get().sendSMS(phone);

          set({ isLoading: false });
          return result;
        } catch (error: any) {
          set({ isLoading: false });
          return { success: false, error: error?.message || 'Ошибка регистрации' };
        }
      },

      // Инициализация API (для совместимости)
      initializeApi: (baseUrl: string) => {
        // API клиент уже инициализирован глобально
        console.log('API клиент уже инициализирован с baseUrl:', apiClient['baseUrl']);
      },

      // Метод login для совместимости
      login: async (phone: string, password?: string) => {
        try {
          set({ isLoading: true });

          // Просто ищем клиента и логиним
          const result = await apiClient.checkPhoneExists(phone);

          if (!result.exists || !result.clientInfo) {
            set({ isLoading: false });
            throw new Error('Пользователь не найден');
          }

          // Загружаем бонусы если это клиент
          let bonusBalance = 0;
          if (result.userType === 'client' && result.clientInfo.ClientId) {
            try {
              const fullInfo = await apiClient.getClient(result.clientInfo.ClientId);
              bonusBalance = fullInfo?.bonus_balance || 0;
            } catch (e) {
              console.error('Ошибка загрузки бонусов:', e);
            }
          }

          const user: User = {
            id: result.clientInfo.ClientId || result.clientInfo.Id,
            firstName: result.clientInfo.first_name,
            lastName: result.clientInfo.last_name,
            middleName: result.clientInfo.middle_name,
            phone: phone,
            role: result.userType === 'employee' ? 'trainer' : 'client',
            bonusBalance: bonusBalance,
            ...(result.userType === 'employee' && {
              specName: result.clientInfo.spec_name,
              department: result.clientInfo.department,
              services: result.clientInfo.services,
              employeeId: result.clientInfo.Id
            })
          };

          set({
            user,
            token: 'session-token',
            isAuthenticated: true,
            isLoading: false
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Метод register для совместимости
      register: async (userData: any) => {
        try {
          set({ isLoading: true });

          await apiClient.createClient({
            first_name: userData.firstName,
            last_name: userData.lastName,
            middle_name: userData.middleName,
            Phone: userData.phone,
            Email: userData.email,
            birthday: userData.birthday
          });

          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage', // Ключ в localStorage
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        // isLoading и apiClient не сохраняем, т.к. это временные состояния
      }),
    }
  )
);

/**
 * ПРИМЕЧАНИЯ ПО ИНТЕГРАЦИИ В UI
 *
 * 1) Экран ввода телефона:
 *    const res = await sendSMS(phone);
 *    if (!res.success && res.userType === 'new') {
 *      // показать регистрацию клиента
 *    }
 *
 * 2) Экран ввода кода:
 *    const res = await verifySMS(code);
 *    if (res.success) {
 *      if (res.userType === 'employee') navigate('/trainer');
 *      else navigate('/app');
 *    }
 *
 * 3) Реальные API: замените getToken/makeRequest/getEmployees/sendSmsCode на ваши
 *
 * 4) Сопоставьте реальные поля сотрудников/клиентов (id, имя/фамилия и т.п.)
 */
